import { useMemo, type CSSProperties } from 'react';
import { HudScrubber } from './HudScrubber';
import { PHASE_OF_PLAY_META, type PhaseOfPlay } from '@4cm/core/phaseOfPlay';

export interface KeyframeMarker {
  /** Position along the sequence, in [0, 1]. */
  t: number;
  /** Coach-visible label rendered under the tick. */
  label: string;
  /** Stable id for keying + click-to-seek (caller resolves). */
  id?: string;
  /** Phase-of-play tag — drives the tick colour so a 6-keyframe play
   *  reads as Build-up (attacking) → Progression (attacking) → Press
   *  trap (defending) etc. at a glance. Undefined = neutral tick. */
  phase?: PhaseOfPlay;
}

interface KeyframeScrubberProps {
  /** Current progress in [0, 1]. */
  value: number;
  /** Called on every scrubber change. */
  onChange: (next: number) => void;
  /** Named keyframe positions to paint under the track. */
  markers: KeyframeMarker[];
  /** ARIA label for the underlying range input. */
  ariaLabel: string;
  /** Optional click-to-seek callback when the coach taps a marker. */
  onMarkerSelect?: (marker: KeyframeMarker) => void;
}

/**
 * Phase 2 — keyframe-aware scrubber. Wraps `HudScrubber` so the
 * gesture / accessibility behaviour stays identical to the rest of the
 * HUD, and overlays a small named tick under each keyframe so the
 * coach can read the named moments of the play at a glance.
 *
 * The tick row is rendered after the scrubber and visually anchored
 * under the track via percentage-positioned spans. Marker labels are
 * also exposed as accessible names on a button per tick so a coach
 * using keyboard / screenreader can jump to a moment without
 * scrubbing pixel-perfect.
 */
export function KeyframeScrubber({
  value,
  onChange,
  markers,
  ariaLabel,
  onMarkerSelect,
}: KeyframeScrubberProps) {
  // Only the keyframe at (or just before) the playhead shows its text
  // label. Painting a label under every tick stacks a dozen "Frame N"
  // strings into an unreadable smear on a long sequence — the ticks
  // alone carry the structure, and every tick keeps its aria-label so
  // keyboard / screen-reader users still get the full set.
  const activeMarkerIndex = useMemo(() => {
    let idx = 0;
    markers.forEach((m, i) => {
      if (m.t <= value) idx = i;
    });
    return idx;
  }, [markers, value]);

  return (
    <div className="thud__kfscrubber" data-testid="kf-scrubber">
      <HudScrubber value={value} onChange={onChange} ariaLabel={ariaLabel} />
      {markers.length > 0 && (
        <div
          className="thud__kfscrubber-markers"
          role="group"
          aria-label={`${ariaLabel} keyframes`}
          style={MARKER_ROW_STYLE}
        >
          {markers.map((m, i) => {
            const pct = Math.max(0, Math.min(1, m.t)) * 100;
            // Phase-tinted tick: pull the canonical hex from the
            // phase-of-play meta. Falls back to the neutral
            // `currentColor` when the keyframe has no phase tag, so
            // legacy layouts (no phase field) keep their original
            // visual treatment.
            const phaseColor = m.phase
              ? PHASE_OF_PLAY_META[m.phase]?.colorHex
              : undefined;
            const tickStyle: CSSProperties = phaseColor
              ? { background: phaseColor }
              : {};
            // Wrapping the tick + label in a button when onMarkerSelect
            // is set keeps the surface keyboard-accessible; otherwise
            // we render an inert span so the markers don't sit in the
            // tab order on read-only HUDs.
            const inner = (
              <>
                <span
                  className="thud__kfscrubber-tick"
                  data-phase={m.phase}
                  style={tickStyle}
                  aria-hidden
                />
                {i === activeMarkerIndex && (
                  <span className="thud__kfscrubber-label">{m.label}</span>
                )}
              </>
            );
            const key = m.id ?? `m-${i}`;
            const style: CSSProperties = {
              ...MARKER_STYLE,
              left: `${pct}%`,
            };
            const ariaSuffix = m.phase ? ` (${m.phase})` : '';
            if (onMarkerSelect) {
              return (
                <button
                  key={key}
                  type="button"
                  className="thud__kfscrubber-marker"
                  style={style}
                  onClick={() => onMarkerSelect(m)}
                  aria-label={`Jump to ${m.label}${ariaSuffix}`}
                >
                  {inner}
                </button>
              );
            }
            return (
              <span
                key={key}
                className="thud__kfscrubber-marker"
                style={style}
                aria-label={`${m.label}${ariaSuffix}`}
              >
                {inner}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Inline styles keep the new HUD primitive self-contained — the
// existing tacticsHud.css will get a polished pass when 2D / 3D wire
// the scrubber in. We anchor every marker absolutely off a relative
// row so percentage positioning aligns with the scrubber track above.
const MARKER_ROW_STYLE: CSSProperties = {
  position: 'relative',
  height: 18,
  marginTop: 2,
  width: '100%',
};

const MARKER_STYLE: CSSProperties = {
  position: 'absolute',
  top: 0,
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  background: 'transparent',
  border: 0,
  padding: 0,
  color: 'inherit',
  cursor: 'pointer',
  font: 'inherit',
};
